import React, { useCallback, type ReactNode } from 'react';
import Link from '@atlaskit/link';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { CONFIG_FILE_UPLOAD_TYPE, CONFIG_MAX_SIZE_BYTES } from '../../../common/constants';
import { Accordion } from '../../../common/ui/accordion';
import { UploadButton } from '../../../common/ui/upload-button';
import {
	useMigrationId,
	useUploadedConfigName,
	useUploadedFileName,
} from '../../../controllers/import-state';
import { useUploadConfiguration } from '../../../services/upload-configuration';
import messages from './messages';

export const UploadConfiguration = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [uploadedFileName] = useUploadedFileName();
	const [uploadedConfigName, { setUploadedConfigName }] = useUploadedConfigName();
	const [migrationId] = useMigrationId();
	const { isFileUploading, hasErrorOccurred, uploadConfiguration, abort } =
		useUploadConfiguration();

	const onReceiveFile = useCallback(
		(file: File) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'configFile uploading', { fileSize: file.size });
			migrationId &&
				uploadConfiguration(file, migrationId).then((isUploaded) => {
					if (isUploaded) {
						setUploadedConfigName(file.name);
						fireTrackAnalytics(createAnalyticsEvent({}), 'configFile uploaded', {
							fileSize: file.size,
						});
					}
				});
		},
		[createAnalyticsEvent, migrationId, setUploadedConfigName, uploadConfiguration],
	);

	const onCancel = useCallback(() => {
		setUploadedConfigName(null);
		abort();
	}, [abort, setUploadedConfigName]);

	return (
		<Box xcss={wrapperStyles}>
			<Accordion heading={messages.uploadConfigurationAccordionHeading} closedAppearance="subtle">
				<Stack space="space.200">
					<Box as="p">
						{formatMessage(messages.uploadConfigurationDescription, {
							link: (content: ReactNode) => <Link href="/">{content}</Link>,
						})}
					</Box>
					<UploadButton
						uploadText={formatMessage(messages.uploadConfiguration)}
						acceptType={CONFIG_FILE_UPLOAD_TYPE}
						onReceiveFile={onReceiveFile}
						onCancel={onCancel}
						fileSizeLimit={CONFIG_MAX_SIZE_BYTES}
						isFileUploading={isFileUploading}
						hasErrorOccured={hasErrorOccurred}
						fileName={uploadedConfigName}
						isDisabled={!uploadedFileName}
					/>
				</Stack>
			</Accordion>
		</Box>
	);
};

const wrapperStyles = xcss({
	paddingBlock: 'space.400',
});
